/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Provider } from "~/generated/graphql";
import { useAccountsOptions } from "~/hooks/useAccountsOptions";
import { getFirstItem, isNotEmpty } from "~/tools";
import { ThresholdDataSelectItem } from "../types";
import { ThresholdFormData, ThresholdFormFields } from "../validators";

export function useSelectAccount() {
  const { control, setValue, trigger } = useFormContext<ThresholdFormData>();
  const change = useWatch({ control, name: ThresholdFormFields.ACCOUNTS });

  const [selectedAccount, setSelectedAccount] = useState<
    ThresholdDataSelectItem | undefined
  >();

  const { accountsOptions, loading, error } = useAccountsOptions([
    Provider.Aws,
    Provider.Azure,
    Provider.Gcp,
  ]);

  const accounts = accountsOptions
    .filter((account) => account.providerType === "CLOUD")
    .map((account) => ({
      value: account.label,
      id: account.value,
    }));

  const onSelect = (account: ThresholdDataSelectItem) => {
    setValue(ThresholdFormFields.ACCOUNTS, [account.id]);
  };

  useEffect(() => {
    setSelectedAccount(accounts.find(({ id }) => id === getFirstItem(change)));

    if (isNotEmpty(change)) {
      trigger(ThresholdFormFields.PROVIDERS);
    }
  }, [change]);

  return {
    accounts,
    selectedAccount,
    loading,
    error,
    onSelect,
  };
}
